import type { IconType } from "react-icons";
import {
  LuGitCompareArrows,
  LuMessageSquareText,
  LuMessagesSquare,
  LuNotebookPen,
  LuWandSparkles,
} from "react-icons/lu";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import type { AssistantPanelTab } from "./AssistantPanel";

const ITEMS: Array<{
  id: AssistantPanelTab;
  label: string;
  icon: IconType;
}> = [
  { id: "comments", label: "Comments", icon: LuMessageSquareText },
  { id: "chat", label: "Chat", icon: LuMessagesSquare },
  { id: "variations", label: "Variations", icon: LuGitCompareArrows },
  { id: "notes", label: "Notes", icon: LuNotebookPen },
  { id: "actions", label: "Actions", icon: LuWandSparkles },
];

export function AssistantModeSwitcher({
  activeId,
  unresolvedCommentCount,
  openVariationCount,
  variationBusy,
  onSelect,
}: {
  activeId: AssistantPanelTab;
  unresolvedCommentCount: number;
  openVariationCount: number;
  variationBusy: boolean;
  onSelect: (id: AssistantPanelTab) => void;
}) {
  const moveSelection = (index: number, direction: -1 | 1) => {
    const nextIndex = (index + direction + ITEMS.length) % ITEMS.length;
    const nextId = ITEMS[nextIndex]!.id;
    onSelect(nextId);
    window.requestAnimationFrame(() => {
      const next = document.querySelector(
        `[data-assistant-mode="${nextId}"]`
      ) as HTMLButtonElement | null;
      next?.focus();
    });
  };

  return (
    <TooltipProvider delayDuration={250}>
      <div
        className="flex w-full shrink-0 items-center gap-1 border-b border-border pb-1.5"
        role="tablist"
        aria-label="Assistant modes"
        data-slot="assistant-mode-switcher"
      >
        {ITEMS.map((item, index) => {
          const active = item.id === activeId;
          const count =
            item.id === "comments"
              ? unresolvedCommentCount
              : item.id === "variations"
                ? openVariationCount
                : 0;
          const tooltip =
            item.id === "variations" && variationBusy
              ? "Variations · generating"
              : count > 0
                ? `${item.label} · ${count}`
                : item.label;
          const Icon = item.icon;

          return (
            <Tooltip key={item.id}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  role="tab"
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "relative h-8 w-auto flex-1 rounded-lg text-muted-foreground",
                    "hover:bg-muted hover:text-foreground",
                    active &&
                      "bg-primary/15 text-primary ring-1 ring-primary/30 ring-inset hover:bg-primary/20 hover:text-primary"
                  )}
                  aria-label={item.label}
                  aria-selected={active}
                  data-assistant-mode={item.id}
                  tabIndex={active ? 0 : -1}
                  onClick={() => onSelect(item.id)}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowLeft") {
                      event.preventDefault();
                      moveSelection(index, -1);
                    } else if (event.key === "ArrowRight") {
                      event.preventDefault();
                      moveSelection(index, 1);
                    }
                  }}
                >
                  <Icon className="size-4" aria-hidden />
                  {variationBusy && item.id === "variations" ? (
                    <span
                      className="absolute right-1 top-1 size-1.5 animate-pulse rounded-full bg-primary"
                      aria-hidden
                    />
                  ) : count > 0 ? (
                    <span className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[0.6rem] font-bold leading-4 text-primary-foreground">
                      {count > 9 ? "9+" : count}
                    </span>
                  ) : null}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={7}>
                {tooltip}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
